import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBriefcase,
  faBug,
  faBullhorn,
  faChartLine,
  faClipboardList,
  faDollarSign,
  faHeadset,
  faHouse,
  faMicrochip,
  faShareNodes,
} from '@fortawesome/free-solid-svg-icons';
import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { User } from '../../types';
import BetaAuthPage from './BetaAuthPage';
import StoreDashboardApisView from './StoreDashboardApisView';
import StoreDashboardFleetView from './StoreDashboardFleetView';
import StoreDashboardMarketingView from './StoreDashboardMarketingView';
import StoreDashboardOverviewView from './StoreDashboardOverviewView';
import StoreDashboardKpiView from './StoreDashboardKpiView';
import StoreDashboardSocialView from './StoreDashboardSocialView';
import StoreDashboardSentryView from './StoreDashboardSentryView';
import StoreDashboardSupportView from './StoreDashboardSupportView';
import StoreDashboardSurveysView from './StoreDashboardSurveysView';
import StoreDashboardView from './StoreDashboardView';
import StoreDashboardWorkView from './StoreDashboardWorkView';
import StoreDashboardGraphBuilderPage from './StoreDashboardGraphBuilderPage';

const DASHBOARD_ROLES = new Set(['admin', 'developer', 'support', 'technician']);

interface StoreDashboardPortalViewProps {
  user: User | null;
  onAuth: (user: User) => void;
  onLogout: () => void;
}

const navClass = ({ isActive }: { isActive: boolean }) =>
  `store-dash-nav-link${isActive ? ' is-active' : ''}`;

const StoreDashboardPortalView: React.FC<StoreDashboardPortalViewProps> = ({
  user,
  onAuth,
  onLogout,
}) => {
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const shopifyParams = new URLSearchParams(location.search);
  if (shopifyParams.get('code') && shopifyParams.get('shop')) {
    window.location.replace(`/shopify/callback${location.search}`);
    return null;
  }

  if (!user) {
    return (
      <div className="store-dash-shell">
        <header className="store-dash-topbar">
          <Link to="/" className="store-dash-brand">
            <img src="/logo-mark.png" alt="" />
            <div>
              <div className="store-dash-brand-kicker">Fluora</div>
              <div className="store-dash-brand-title">Dashboard</div>
            </div>
          </Link>
          <Link to="/login" className="btn btn-outline-secondary btn-sm">Log in</Link>
        </header>
        <Routes>
          <Route
            path="/login"
            element={(
              <BetaAuthPage
                mode="login"
                onAuth={onAuth}
                allowRegister={false}
                lead="Sign in with a Fluora staff account to view store metrics."
              />
            )}
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    );
  }

  if (!DASHBOARD_ROLES.has(user.role)) {
    return (
      <div className="store-dash-shell">
        <header className="store-dash-topbar">
          <Link to="/" className="store-dash-brand">
            <img src="/logo-mark.png" alt="" />
            <div>
              <div className="store-dash-brand-kicker">Fluora</div>
              <div className="store-dash-brand-title">Dashboard</div>
            </div>
          </Link>
        </header>
        <main className="store-dash-denied">
          <h1>You don’t have access</h1>
          <p>This dashboard is for Fluora staff. Factory and customer accounts cannot view store metrics.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="store-dash-shell store-dash-app">
      {navOpen && <button type="button" className="store-dash-nav-backdrop" aria-label="Close menu" onClick={() => setNavOpen(false)} />}
      <aside className={`store-dash-sidebar${navOpen ? ' is-open' : ''}`}>
        <Link to="/" className="store-dash-brand" onClick={() => setNavOpen(false)}>
          <img src="/logo-mark.png" alt="" />
          <div>
            <div className="store-dash-brand-kicker">Fluora</div>
            <div className="store-dash-brand-title">Dashboard</div>
          </div>
        </Link>
        <nav className="store-dash-nav">
          <NavLink to="/" end className={navClass} onClick={() => setNavOpen(false)}>
            <FontAwesomeIcon icon={faHouse} />
            Home
          </NavLink>
          <NavLink to="/kpis" className={navClass} onClick={() => setNavOpen(false)}>
            <FontAwesomeIcon icon={faChartLine} />
            KPIs
          </NavLink>
          <NavLink to="/sales" className={navClass} onClick={() => setNavOpen(false)}>
            <FontAwesomeIcon icon={faDollarSign} />
            Sales
          </NavLink>
          <NavLink to="/marketing" className={navClass} onClick={() => setNavOpen(false)}>
            <FontAwesomeIcon icon={faBullhorn} />
            Marketing
          </NavLink>
          <NavLink to="/social" className={navClass} onClick={() => setNavOpen(false)}>
            <FontAwesomeIcon icon={faShareNodes} />
            Social
          </NavLink>
          <NavLink to="/fleet" className={navClass} onClick={() => setNavOpen(false)}>
            <FontAwesomeIcon icon={faMicrochip} />
            Fleet
          </NavLink>
          <NavLink to="/work" className={navClass} onClick={() => setNavOpen(false)}>
            <FontAwesomeIcon icon={faBriefcase} />
            Work
          </NavLink>
          <NavLink to="/support" className={navClass} onClick={() => setNavOpen(false)}>
            <FontAwesomeIcon icon={faHeadset} />
            Support
          </NavLink>
          <NavLink to="/sentry" className={navClass} onClick={() => setNavOpen(false)}>
            <FontAwesomeIcon icon={faBug} />
            Sentry
          </NavLink>
          <NavLink to="/surveys" className={navClass} onClick={() => setNavOpen(false)}>
            <FontAwesomeIcon icon={faClipboardList} />
            Surveys
          </NavLink>
          <NavLink
            to="/apis"
            className={({ isActive }) => `${navClass({ isActive })} is-muted`}
            onClick={() => setNavOpen(false)}
          >
            APIs
          </NavLink>
        </nav>
        <div className="store-dash-sidebar-footer">
          <span>{user.firstName} {user.lastName}</span>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </aside>
      <div className="store-dash-content">
        <header className="store-dash-mobilebar">
          <button type="button" className="store-dash-menu" onClick={() => setNavOpen(true)}>
            Menu
          </button>
          <span>Fluora Dashboard</span>
        </header>
        <main className="store-dash-main">
          <Routes>
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/apis" element={<StoreDashboardApisView />} />
            <Route path="/sales" element={<StoreDashboardView />} />
            <Route path="/marketing" element={<StoreDashboardMarketingView />} />
            <Route path="/social" element={<StoreDashboardSocialView />} />
            <Route path="/fleet" element={<StoreDashboardFleetView />} />
            <Route path="/support" element={<StoreDashboardSupportView />} />
            <Route path="/sentry" element={<StoreDashboardSentryView />} />
            <Route path="/work" element={<StoreDashboardWorkView />} />
            <Route path="/surveys" element={<StoreDashboardSurveysView />} />
            <Route path="/custom/new" element={<StoreDashboardGraphBuilderPage />} />
            <Route path="/custom/:id" element={<StoreDashboardGraphBuilderPage />} />
            <Route path="/kpis" element={<StoreDashboardKpiView user={user} />} />
            <Route path="/" element={<StoreDashboardOverviewView />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default StoreDashboardPortalView;
